/* WebRTC Softphone (full-screen Yeastar Linkus-style page).
 *
 * Thin consumer of `useSoftphone()` — all SIP UA logic lives in
 * SoftphoneContext (mounted once at App root), so this page can be
 * navigated to / away from without losing registration or in-progress
 * calls. The shared FloatingDialer in Layout consumes the same context.
 */
import React from 'react';
import {
    Phone, PhoneSlash, MicrophoneSlash, Microphone, Pause, Play,
    SpinnerGap, CheckCircle, WarningCircle, ArrowsClockwise, UserCircle,
} from '@phosphor-icons/react';
import { useSoftphone } from '@/context/SoftphoneContext';

const STATE_LABELS = {
    idle: 'Připraveno',
    connecting: 'Připojuji…',
    registered: 'Registrováno',
    failed: 'Chyba registrace',
    ringing: 'Vyzvání',
    in_call: 'Hovor',
    ended: 'Ukončeno',
};

const DialButton = ({ digit, sub, onClick, dtid }) => (
    <button
        data-testid={dtid}
        onClick={() => onClick(digit)}
        className="aspect-square flex flex-col items-center justify-center rounded-2xl
                   bg-[var(--bg-surface)] hover:bg-[var(--bg-surface-hover)]
                   border border-[var(--border-default)] transition-all
                   active:scale-95 select-none"
    >
        <span className="text-3xl font-light">{digit}</span>
        {sub && <span className="text-[10px] tracking-[0.25em] uppercase mt-0.5
                              text-[var(--text-secondary)]">{sub}</span>}
    </button>
);

const KEYS = [
    ['1', ''], ['2', 'ABC'], ['3', 'DEF'],
    ['4', 'GHI'], ['5', 'JKL'], ['6', 'MNO'],
    ['7', 'PQRS'], ['8', 'TUV'], ['9', 'WXYZ'],
    ['*', ''], ['0', '+'], ['#', ''],
];

const fmt = (sec) => {
    const m = Math.floor(sec / 60).toString().padStart(2, '0');
    const s = (sec % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
};

const Softphone = () => {
    const {
        creds, credsError, phase, dest, setDest, muted, held, duration, remote, hasIncoming,
        register, unregister, placeCall, hangup, acceptCall, toggleMute, toggleHold, sendDtmf,
    } = useSoftphone();

    const phaseColor =
        phase === 'registered' ? 'var(--success)' :
        phase === 'in_call' ? 'var(--brand)' :
        phase === 'failed' ? 'var(--danger)' :
        phase === 'ringing' ? '#f59e0b' : 'var(--text-secondary)';

    if (credsError) {
        return (
            <div className="max-w-md mx-auto mt-12 p-8 rounded-xl border text-center"
                 data-testid="softphone-no-extension"
                 style={{ borderColor: 'var(--border-default)', background: 'var(--bg-surface)' }}>
                <WarningCircle size={48} className="mx-auto mb-4" style={{ color: 'var(--danger)' }} />
                <h2 className="text-lg font-bold mb-2">Softphone není připraven</h2>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{credsError}</p>
            </div>
        );
    }
    if (!creds) {
        return (
            <div className="flex justify-center mt-20" data-testid="softphone-loading">
                <SpinnerGap size={32} className="animate-spin" />
            </div>
        );
    }

    return (
        <div className="max-w-md mx-auto" data-testid="softphone-container">
            <div className="mb-6">
                <h1 className="text-3xl font-heading font-bold">Softphone</h1>
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                    Yeastar Linkus-style klient v prohlížeči
                </p>
            </div>

            <div className="rounded-2xl border p-6 mb-4"
                 style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <UserCircle size={20} style={{ color: 'var(--brand)' }} />
                        <span className="font-mono text-sm" data-testid="sp-ext">{creds.extension}</span>
                        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                            {creds.display_name}
                        </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider"
                         data-testid="sp-status" style={{ color: phaseColor }}>
                        {phase === 'registered' || phase === 'in_call'
                            ? <CheckCircle size={14} weight="fill" />
                            : phase === 'failed'
                            ? <WarningCircle size={14} weight="fill" />
                            : <SpinnerGap size={14} className={phase === 'connecting' ? 'animate-spin' : ''} />}
                        {STATE_LABELS[phase] || phase}
                    </div>
                </div>

                {(phase === 'idle' || phase === 'failed' || phase === 'connecting') && (
                    <button
                        data-testid="sp-register-btn"
                        onClick={register}
                        disabled={phase === 'connecting'}
                        className="mt-3 w-full py-2.5 rounded-lg font-bold text-sm uppercase tracking-wider
                                   disabled:opacity-50"
                        style={{ background: 'var(--brand)', color: '#000' }}
                    >
                        {phase === 'connecting' ? 'Připojuji…' : 'Připojit'}
                    </button>
                )}
                {(phase === 'registered' || phase === 'in_call' || phase === 'ringing') && (
                    <button
                        data-testid="sp-unregister-btn"
                        onClick={unregister}
                        className="mt-3 w-full py-2 rounded-lg text-xs uppercase tracking-wider
                                   border border-[var(--border-default)]
                                   hover:border-[var(--danger)] hover:text-[var(--danger)]"
                    >
                        <ArrowsClockwise size={14} className="inline mr-1" /> Odregistrovat
                    </button>
                )}
            </div>

            {(phase === 'in_call' || phase === 'ringing') && (
                <div className="rounded-2xl border p-6 mb-4 text-center"
                     data-testid="sp-active-call"
                     style={{ background: 'var(--bg-surface)', borderColor: 'var(--brand)' }}>
                    <div className="text-xs uppercase tracking-[0.2em] mb-2"
                         style={{ color: 'var(--text-secondary)' }}>
                        {phase === 'ringing' ? 'Příchozí / Vyzvání' : 'Aktivní hovor'}
                    </div>
                    <div className="text-2xl font-mono font-bold mb-1">{remote}</div>
                    {phase === 'in_call' && (
                        <div className="text-sm font-mono" style={{ color: 'var(--brand)' }}>
                            {fmt(duration)}
                        </div>
                    )}
                </div>
            )}

            <div className="rounded-2xl border p-6"
                 style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                <input
                    data-testid="sp-dest-input"
                    value={dest}
                    onChange={(e) => setDest(e.target.value)}
                    placeholder="Zadejte číslo nebo klapku…"
                    className="w-full mb-4 bg-transparent border-0 border-b py-2 text-2xl
                               font-mono text-center focus:outline-none
                               focus:border-[var(--brand)] transition-colors"
                    style={{ borderColor: 'var(--border-default)' }}
                />
                <div className="grid grid-cols-3 gap-2 mb-4">
                    {KEYS.map(([d, s]) => (
                        <DialButton key={d} digit={d} sub={s}
                                    onClick={sendDtmf}
                                    dtid={`sp-key-${d === '*' ? 'star' : d === '#' ? 'hash' : d}`} />
                    ))}
                </div>

                <div className="grid grid-cols-3 gap-2">
                    <button
                        data-testid="sp-mute-btn"
                        onClick={toggleMute}
                        disabled={phase !== 'in_call'}
                        className="py-3 rounded-xl border disabled:opacity-30
                                   hover:bg-[var(--bg-surface-hover)] flex items-center justify-center"
                        style={{ borderColor: muted ? 'var(--danger)' : 'var(--border-default)' }}
                    >
                        {muted ? <MicrophoneSlash size={20} color="var(--danger)" />
                               : <Microphone size={20} />}
                    </button>

                    {phase === 'ringing' && hasIncoming ? (
                        <button
                            data-testid="sp-accept-btn"
                            onClick={acceptCall}
                            className="py-3 rounded-xl text-white font-bold flex items-center justify-center"
                            style={{ background: 'var(--success)' }}
                        >
                            <Phone size={22} weight="fill" />
                        </button>
                    ) : phase === 'in_call' || phase === 'ringing' ? (
                        <button
                            data-testid="sp-hangup-btn"
                            onClick={hangup}
                            className="py-3 rounded-xl text-white font-bold flex items-center justify-center"
                            style={{ background: 'var(--danger)' }}
                        >
                            <PhoneSlash size={22} weight="fill" />
                        </button>
                    ) : (
                        <button
                            data-testid="sp-call-btn"
                            onClick={() => placeCall()}
                            disabled={phase !== 'registered'}
                            className="py-3 rounded-xl text-black font-bold disabled:opacity-30
                                       flex items-center justify-center"
                            style={{ background: 'var(--brand)' }}
                        >
                            <Phone size={22} weight="fill" />
                        </button>
                    )}

                    <button
                        data-testid="sp-hold-btn"
                        onClick={toggleHold}
                        disabled={phase !== 'in_call'}
                        className="py-3 rounded-xl border disabled:opacity-30
                                   hover:bg-[var(--bg-surface-hover)] flex items-center justify-center"
                        style={{ borderColor: held ? 'var(--brand)' : 'var(--border-default)' }}
                    >
                        {held ? <Play size={20} color="var(--brand)" /> : <Pause size={20} />}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Softphone;
